import { getLeads, type Lead } from '@/lib/api/sales/leads-api'
import { startSalesFlowRun, type SalesFlowNode, type SalesFlowRun } from '@/lib/api/sales/flows-api'
import { useEffect, useState } from 'react'
import { MdClose, MdPlayArrow, MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'
import RunLogTimeline from './run-log-timeline'

/**
 * Simula o fluxo publicado para um lead: mostra o caminho e as mensagens
 * renderizadas, sem enviar nem alterar nada (execução `dryRun`).
 */
export default function SimulateModal({
    flowId,
    nodes,
    dirty,
    onClose,
}: {
    flowId: number
    nodes: SalesFlowNode[]
    dirty: boolean
    onClose: () => void
}) {
    const [search, setSearch] = useState('')
    const [leads, setLeads] = useState<Lead[]>([])
    const [lead, setLead] = useState<Lead | null>(null)
    const [running, setRunning] = useState(false)
    const [run, setRun] = useState<SalesFlowRun | null>(null)

    useEffect(() => {
        if (search.trim().length < 2) {
            setLeads([])
            return
        }
        const timer = setTimeout(() => {
            getLeads({ search: search.trim(), limit: 8 }).then(res => setLeads(res.success ? res.result?.items ?? [] : []))
        }, 300)
        return () => clearTimeout(timer)
    }, [search])

    const simulate = async () => {
        if (!lead) return
        setRunning(true)
        const res = await startSalesFlowRun(flowId, { leadId: lead.id, dryRun: true })
        setRunning(false)
        if (!res.success) {
            toast.error(res.error || 'Não foi possível simular')
            return
        }
        setRun(res.result)
    }

    return (
        <dialog className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold">Simular fluxo</h3>
                        <p className="mt-1 text-sm text-base-content/60">Mostra o caminho e as mensagens para um lead. Nada é enviado nem alterado.</p>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Fechar">
                        <MdClose />
                    </button>
                </div>

                {dirty && <div className="alert alert-warning mb-4 py-2 text-sm">Há um rascunho não publicado: a simulação usa a versão publicada.</div>}

                <div className="space-y-2">
                    <label htmlFor="simulate-lead" className="text-sm font-medium">
                        Lead
                    </label>
                    {lead ? (
                        <div className="flex items-center justify-between rounded-lg border border-base-300 px-3 py-2">
                            <span className="text-sm">
                                {lead.name} <span className="text-base-content/50">· {lead.phoneNumber}</span>
                            </span>
                            <button type="button" className="btn btn-ghost btn-xs" onClick={() => { setLead(null); setRun(null) }}>
                                Trocar
                            </button>
                        </div>
                    ) : (
                        <>
                            <label className="input input-bordered input-sm flex items-center gap-2">
                                <MdSearch className="text-base-content/40" />
                                <input id="simulate-lead" className="grow" placeholder="Nome ou telefone" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                            </label>
                            {leads.length > 0 && (
                                <ul className="menu rounded-lg border border-base-300 bg-base-100 p-1">
                                    {leads.map(item => (
                                        <li key={item.id}>
                                            <button type="button" onClick={() => setLead(item)}>
                                                {item.name} <span className="text-base-content/50">{item.phoneNumber}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>

                {run && (
                    <div className="mt-6 space-y-3">
                        <h4 className="text-sm font-semibold">Caminho</h4>
                        <RunLogTimeline logs={run.logs ?? []} nodes={nodes} />
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Fechar
                    </button>
                    <button type="button" className="btn btn-primary gap-1" disabled={!lead || running} onClick={simulate}>
                        {running ? <span className="loading loading-spinner loading-xs" /> : <MdPlayArrow />}
                        Simular
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </dialog>
    )
}
